import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const ORB_URL = "/hypseya-wordmark.png";

const INSTITUTIONS = [
  { region: 'UK', name: 'Imperial College London', desc: 'Centre for Psychedelic Research' },
  { region: 'CH', name: 'University of Basel · SÄPT', desc: 'Pharmacology & Neuroscience · legal psychedelic therapy programme' },
  { region: 'EU', name: 'MAPS Europe', desc: 'MDMA-assisted therapy · integration support' },
  { region: 'UK', name: "King's College London", desc: 'Psilocybin for treatment-resistant depression' },
  { region: 'NL', name: 'University of Amsterdam', desc: 'Psilocybin & neuroplasticity research' },
  { region: 'PT', name: 'Portuguese institutions', desc: 'Decriminalisation context · emerging research environment' },
];

const METRICS = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="10" width="3.5" height="6" rx="1" stroke="#2D8B87" strokeWidth="1.3"/>
        <rect x="7.5" y="6" width="3.5" height="10" rx="1" stroke="#2D8B87" strokeWidth="1.3"/>
        <rect x="13" y="2" width="3.5" height="14" rx="1" stroke="#2D8B87" strokeWidth="1.3"/>
      </svg>
    ),
    label: 'ERI — Emotional Regulation Index',
    desc: 'Proprietary daily composite score. Longitudinal trends across 14, 30 and 90 days. The missing continuous measure in psychedelic trial design.',
    color: '#1E6B68',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="14" height="14" rx="3" stroke="#6B5B8A" strokeWidth="1.3"/>
        <path d="M5 6H13M5 9H13M5 12H9" stroke="#6B5B8A" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
    ),
    label: 'PHQ-9 & GAD-7',
    desc: 'The validated clinical scales your IRB already approves — embedded into daily practice for higher completion rates than standalone assessment.',
    color: '#6B5B8A',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M1 10L3.5 6L6 11L8.5 3L11 12L13.5 7L16 9" stroke="#C9983E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    label: 'HRV & Biometrics',
    desc: 'Heart rate variability trend and sleep quality from Apple Health / Google Fit. Objective biological markers of nervous system recovery during integration.',
    color: '#C9983E',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke="#2D8B87" strokeWidth="1.3"/>
        <path d="M6 9.5C6 9.5 7 11.5 9 11.5C11 11.5 12 9.5 12 9.5" stroke="#2D8B87" strokeWidth="1.2" strokeLinecap="round"/>
        <circle cx="6.5" cy="7" r="1" fill="#2D8B87"/>
        <circle cx="11.5" cy="7" r="1" fill="#2D8B87"/>
      </svg>
    ),
    label: 'Pattern Intelligence',
    desc: 'AI-generated summaries of emotional patterns across the integration window. Qualitative analysis at population scale — impossible with manual review.',
    color: '#2D8B87',
  },
];

const TIMELINE = [
  { key: 'T−7', phase: 'Baseline', desc: 'ERI · PHQ-9 · GAD-7 · HRV baseline', active: false, session: false },
  { key: 'T0',  phase: 'Session',  desc: 'Pre-session nervous system state',     active: false, session: true  },
  { key: 'T+1 → T+30', phase: 'Integration window', desc: 'Daily Loop · ERI tracking · biometric monitoring · therapist visibility', active: true, session: false },
  { key: 'T+90', phase: 'Consolidation', desc: 'Sustained ERI · PHQ-9/GAD-7 · long-term integration quality', active: false, session: false },
];

export default function Research() {
  const navigate = useNavigate();
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formInstitution, setFormInstitution] = useState('');
  const [formArea, setFormArea] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    window.location.href = `mailto:helena@hypseya.com?subject=Research Partnership — ${formInstitution}&body=Name: ${formName}%0AInstitution: ${formInstitution}%0AResearch area: ${formArea}%0AEmail: ${formEmail}`;
    setSent(true);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0A1614',
      fontFamily: "'Inter', sans-serif",
      color: 'white',
      paddingBottom: 80,
    }}>
      {/* Nav */}
      <div style={{ padding: '24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(184,211,207,0.06)' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'rgba(184,211,207,0.45)', fontSize: '0.8rem', cursor: 'pointer', letterSpacing: '0.05em' }}
        >
          ← HYPSEYA
        </button>
        <span style={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.3)' }}>
          Research Partnership
        </span>
        <a href="mailto:helena@hypseya.com" style={{ fontSize: '0.78rem', color: 'rgba(30,220,210,0.7)', textDecoration: 'none' }}>
          helena@hypseya.com
        </a>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px' }}>

        {/* ── Hero ── */}
        <div style={{ padding: '72px 0 56px', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
            {['Neuroscience', 'Psychedelic-assisted therapy', 'Integration research'].map(t => (
              <span key={t} style={{ padding: '5px 14px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', background: 'rgba(107,91,138,0.18)', color: 'rgba(139,122,170,0.9)' }}>{t}</span>
            ))}
          </div>

          <div style={{ position: 'relative', display: 'inline-block', marginBottom: 36 }}>
            <div style={{ position: 'absolute', inset: -40, borderRadius: '50%', background: 'radial-gradient(ellipse,rgba(107,91,138,0.12) 0%,transparent 70%)', animation: 'orbFloat 5s ease-in-out infinite' }} />
            <img src={ORB_URL} alt="" style={{ width: 110, opacity: 0.85, filter: 'drop-shadow(0 0 30px rgba(107,91,138,0.3))', position: 'relative' }} />
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            fontWeight: 500, lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '0 0 20px',
          }}>
            The integration layer<br />
            <em style={{ color: 'rgba(139,122,170,0.9)' }}>your trials are missing.</em>
          </h1>

          <p style={{ fontSize: 'clamp(0.9rem,1.4vw,1.05rem)', lineHeight: 1.75, color: 'rgba(255,255,255,0.52)', maxWidth: 560, margin: '0 auto 32px' }}>
            Psychedelic-assisted therapy opens a neuroplasticity window of 30 days after each session. HYPSEYA is the only platform built to measure and train that window — continuously, longitudinally, and at research grade.
          </p>

          <a href="#contact" style={{
            display: 'inline-block',
            padding: '14px 32px',
            background: 'linear-gradient(135deg, rgba(107,91,138,0.4) 0%, rgba(30,107,104,0.4) 100%)',
            border: '1px solid rgba(139,122,170,0.3)',
            borderRadius: 100,
            color: 'white',
            textDecoration: 'none',
            fontSize: '0.88rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
          }}>
            Propose a research partnership →
          </a>
        </div>

        {/* ── The gap ── */}
        <div style={{ marginBottom: 64 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 16 }}>The research gap</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.4rem,3vw,2rem)', fontWeight: 500, lineHeight: 1.2, marginBottom: 24 }}>
            You measure what happens <em style={{ color: 'rgba(139,122,170,0.8)' }}>in</em> the session.<br />
            Nobody measures what happens <em style={{ color: 'rgba(30,220,210,0.8)' }}>after.</em>
          </h2>
          <p style={{ fontSize: '0.92rem', lineHeight: 1.72, color: 'rgba(255,255,255,0.5)', maxWidth: 620 }}>
            Existing trial protocols capture PHQ-9 / GAD-7 at T0 and T+4 weeks. They miss 30 days of daily emotional fluctuation, biometric recovery, and practice consistency — the very data that predicts long-term outcomes. HYPSEYA closes that gap without disrupting your existing design.
          </p>
        </div>

        {/* ── Timeline ── */}
        <div style={{ marginBottom: 64 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 20 }}>Measurement design</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {TIMELINE.map((t, i) => (
              <div key={i} style={{
                display: 'grid',
                gridTemplateColumns: '100px 110px 1fr',
                gap: 16,
                padding: '14px 20px',
                borderRadius: 14,
                background: t.active ? 'rgba(30,107,104,0.12)' : t.session ? 'rgba(201,152,62,0.08)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${t.active ? 'rgba(30,200,190,0.2)' : t.session ? 'rgba(201,152,62,0.18)' : 'rgba(255,255,255,0.05)'}`,
                alignItems: 'center',
              }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '0.95rem', fontWeight: 700, color: t.active ? 'rgba(30,220,210,0.9)' : t.session ? '#D4A855' : 'rgba(184,211,207,0.45)' }}>{t.key}</div>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.active ? 'rgba(30,200,190,0.6)' : t.session ? 'rgba(201,152,62,0.6)' : 'rgba(184,211,207,0.25)' }}>{t.phase}</div>
                <div style={{ fontSize: '0.78rem', color: t.active ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.38)', lineHeight: 1.5 }}>
                  {t.active && <strong style={{ color: 'rgba(30,220,210,0.9)', display: 'block', marginBottom: 2 }}>HYPSEYA operates here.</strong>}
                  {t.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── What we measure ── */}
        <div style={{ marginBottom: 64 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 16 }}>Research instruments</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.3rem,2.5vw,1.8rem)', fontWeight: 500, lineHeight: 1.2, marginBottom: 28 }}>
            Every metric your protocol needs. Collected continuously.
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
            {METRICS.map((m) => (
              <div key={m.label} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 18, padding: '22px 24px', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${m.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  {m.icon}
                </div>
                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(255,255,255,0.82)', marginBottom: 8, lineHeight: 1.3 }}>{m.label}</div>
                <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.42)', lineHeight: 1.65 }}>{m.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, padding: '12px 20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', textAlign: 'center', lineHeight: 1.6 }}>
            All data exportable as anonymised CSV or via API · GDPR special category compliant · DPAs provided as standard · Journal entries encrypted and never shared
          </div>
        </div>

        {/* ── Partnership ── */}
        <div style={{ marginBottom: 64 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 16 }}>Partnership terms</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ background: 'rgba(107,91,138,0.1)', borderRadius: 18, padding: '24px', border: '1px solid rgba(139,122,170,0.15)' }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(139,122,170,0.6)', marginBottom: 14 }}>What your institution gets</div>
              {['Full access to participant-level anonymised data', 'Co-authorship on resulting publications', 'HYPSEYA as a cited integration tool in your protocol', 'Horizon Europe / UKRI grant co-application eligibility', 'Structured integration support (participant duty of care)'].map(item => (
                <div key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(139,122,170,0.7)', flexShrink: 0, marginTop: 7 }} />
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.45 }}>{item}</span>
                </div>
              ))}
            </div>
            <div style={{ background: 'rgba(30,107,104,0.08)', borderRadius: 18, padding: '24px', border: '1px solid rgba(30,200,190,0.12)' }}>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(30,200,190,0.5)', marginBottom: 14 }}>What HYPSEYA asks for</div>
              {['Ethics committee / IRB review of HYPSEYA data model', 'Participant consent for digital integration tool', 'Session dates to align measurement windows', 'Access to aggregate anonymised dataset for ERI validation', 'Co-authorship on publications arising from the data'].map(item => (
                <div key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(30,210,200,0.6)', flexShrink: 0, marginTop: 7 }} />
                  <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.58)', lineHeight: 1.45 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Institutions ── */}
        <div style={{ marginBottom: 64 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 16 }}>Target research institutions</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {INSTITUTIONS.map((inst) => (
              <div key={inst.name} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '14px 18px', background: 'rgba(255,255,255,0.025)', borderRadius: 14, border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'rgba(184,211,207,0.3)', letterSpacing: '0.06em', flexShrink: 0, width: 24, textAlign: 'right' }}>{inst.region}</div>
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>{inst.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.32)', marginTop: 2 }}>{inst.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Contact form ── */}
        <div id="contact" style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 24, padding: '40px 36px', border: '1px solid rgba(184,211,207,0.08)', marginBottom: 40 }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.35)', marginBottom: 12 }}>Get in touch</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.3rem,2.5vw,1.9rem)', fontWeight: 500, lineHeight: 1.2, marginBottom: 10 }}>
              Let's design the protocol <em style={{ color: 'rgba(139,122,170,0.85)' }}>together.</em>
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, maxWidth: 420, margin: '0 auto' }}>
              Tell us about your research context and we'll set up an initial conversation.
            </p>
          </div>

          {sent ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: 'rgba(30,220,210,0.8)', marginBottom: 8 }}>Your email client should have opened.</div>
              <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)' }}>If not, write directly to <a href="mailto:helena@hypseya.com" style={{ color: 'rgba(30,220,210,0.7)' }}>helena@hypseya.com</a></p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.4)', marginBottom: 8 }}>Your name</label>
                  <input
                    type="text" required value={formName} onChange={e => setFormName(e.target.value)}
                    placeholder="Dr. Jane Smith"
                    style={{ width: '100%', padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(184,211,207,0.12)', borderRadius: 12, color: 'white', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.4)', marginBottom: 8 }}>Your email</label>
                  <input
                    type="email" required value={formEmail} onChange={e => setFormEmail(e.target.value)}
                    placeholder="you@institution.edu"
                    style={{ width: '100%', padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(184,211,207,0.12)', borderRadius: 12, color: 'white', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.4)', marginBottom: 8 }}>Institution</label>
                <input
                  type="text" required value={formInstitution} onChange={e => setFormInstitution(e.target.value)}
                  placeholder="University / Research Centre / Foundation"
                  style={{ width: '100%', padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(184,211,207,0.12)', borderRadius: 12, color: 'white', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(184,211,207,0.4)', marginBottom: 8 }}>Research area & context</label>
                <textarea
                  required value={formArea} onChange={e => setFormArea(e.target.value)}
                  placeholder="Tell us briefly about your research focus, current trials or projects, and what you're exploring..."
                  rows={4}
                  style={{ width: '100%', padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(184,211,207,0.12)', borderRadius: 12, color: 'white', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: "'Inter', sans-serif" }}
                />
              </div>
              <button type="submit" style={{
                width: '100%', padding: '16px',
                background: 'linear-gradient(135deg, rgba(107,91,138,0.5) 0%, rgba(30,107,104,0.5) 100%)',
                border: '1px solid rgba(139,122,170,0.3)',
                borderRadius: 16, color: 'white', fontSize: '0.9rem', fontWeight: 600,
                cursor: 'pointer', letterSpacing: '0.04em',
              }}>
                Propose a research partnership →
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', paddingBottom: 20 }}>
          <p style={{ fontSize: '0.7rem', color: 'rgba(184,211,207,0.25)', letterSpacing: '0.1em' }}>
            HYPSEYA · hypseya.com · helena@hypseya.com · Built with GDPR principles · Mental health data protected
          </p>
        </div>

      </div>

      <style>{`
        @keyframes orbFloat { 0%,100%{transform:translateY(0);} 50%{transform:translateY(-8px);} }
        input::placeholder, textarea::placeholder { color: rgba(184,211,207,0.2); }
        input:focus, textarea:focus { border-color: rgba(30,200,190,0.3) !important; }
      `}</style>
    </div>
  );
}
